import { Injectable, inject } from '@angular/core';
import {
  ConfirmationService,
  SelectItem,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { map, Observable, tap } from 'rxjs';
import { WarehouseLocalizationCollectionItem } from '../models/warehouse-localization-collection-item';
import { WarehouseLocalizationApiService } from '../api/services/warehouse-localization-api.service';
import { CollectionRequestBuilder } from '@web/common';
import { WarehouseLocalizationCollectionFilter } from '../models/warehouse-localization-collection-filter';
import { WarehouseLocalizationDetailsForm } from '../models/warehouse-localization-details-form';
import { Guid, once } from '@web/utils';
import { WarehouseLocalizationUpdateRequest } from '../api/requests/warehouse-localization-update.request';
import { WarehouseLocalizationCreateRequest } from '../api/requests/warehouse-localization-create.request';

@Injectable({
  providedIn: 'root',
})
export class WarehouseLocalizationDataSourceService {
  /* prettier-ignore */
  private readonly warehouseLocalizationApi = inject(WarehouseLocalizationApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<WarehouseLocalizationCollectionFilter>,
  ): Observable<TableDataPayload<WarehouseLocalizationCollectionItem>> {
    return this.warehouseLocalizationApi
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('label').build(),
              )
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload<WarehouseLocalizationCollectionItem>(
              response.items.map(
                (item) =>
                  new WarehouseLocalizationCollectionItem({
                    id: item.id,
                    label: item.label,
                    createdAt: item.createdAt,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsFormById(
    id: Guid,
  ): Observable<WarehouseLocalizationDetailsForm> {
    return this.warehouseLocalizationApi
      .fetchDetailsById(id)
      .pipe(
        map(
          (response) =>
            new WarehouseLocalizationDetailsForm(
              response.label,
              response.position,
            ),
        ),
      );
  }

  public updateByFromData(
    id: Guid,
    data: WarehouseLocalizationDetailsForm,
  ): Observable<void> {
    return this.warehouseLocalizationApi
      .update(
        id,
        new WarehouseLocalizationUpdateRequest(data.label, data.position),
      )
      .pipe(
        tap(() =>
          this.toast.show((builder) =>
            builder
              .initSuccess('Lokalizacja została zaktualizowana pomyślnie.')
              .build(),
          ),
        ),
      );
  }

  public createByFormData(
    data: WarehouseLocalizationDetailsForm,
  ): Observable<void> {
    return this.warehouseLocalizationApi
      .create(new WarehouseLocalizationCreateRequest(data.label))
      .pipe(
        tap(() =>
          this.toast.show((builder) =>
            builder
              .initSuccess('Lokalizacja została dodana pomyślnie.')
              .build(),
          ),
        ),
      );
  }

  public confirmAndRemove(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz usunąć wybraną lokalizację w magazynie?',
          )
          .accept(() =>
            this.warehouseLocalizationApi
              .remove(id)
              .pipe(
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrany element został pomyślnie usunięty.')
                      .build(),
                  ),
                ),
                once(),
              )
              .subscribe(subscriber),
          )
          .build(),
      );
    });
  }

  public fetchCollectionAsSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.warehouseLocalizationApi
      .fetchCollection(
        this.collectionRequestBuilder.disablePagination().build(),
        true,
      )
      .pipe(
        map((response) =>
          response.items.map((item) => new SelectItem(item.label, item.id)),
        ),
      );
  }
}
